import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';
import { getApiKey, getSecretKey } from '../firebaseConfig';

const PaymentInterface = ({ total, currency, orderID, name, email, phone, onClose, onPay, cartItems, address }) => {
  const [apiKey, setApiKey] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadResources = async () => {
      const start = Date.now();

      
      try {
        // const apikey = 'rzp_test_rfiJegOOywHocU	'
        const apikey = await getApiKey();
        if (mounted) setApiKey(apikey);
      } catch (error) {
        console.error('Error fetching API key:', error);
        if (mounted) setReady(true); // Allow proceeding even if API key fetch fails
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        if (mounted) {
          const elapsed = Date.now() - start;
          const remaining = 2000 - elapsed;
          setTimeout(() => setReady(true), remaining > 0 ? remaining : 0);
        }
      };
      script.onerror = () => {
        if (mounted) {
          console.error('Failed to load Razorpay script');
          setReady(true); // Allow proceeding to show error on button click
        }
      };
      document.body.appendChild(script);

      return () => {
        mounted = false;
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    };

    loadResources();
  }, []);

  const handlePayment = () => {
    if (!apiKey || !window.Razorpay) {
      alert('Payment service unavailable. Please try again later.');
      setLoading(false);
      return;
    }

    setPaymentStatus(null);
    setLoading(true);

    const options = {
      key: apiKey,
      amount: Math.round(total * 100),
      currency,
      name: 'Laynered',
      description: `#${orderID}`,
      handler: (response) => {
        setPaymentStatus('success');
        setLoading(false);
        onPay(true);
        setTimeout(onClose, 2000);
      },
      prefill: {
        name: name || '',
        email: email || '',
        contact: phone || '',
      },
      notes: {
        order_id: orderID,
        address: JSON.stringify(address),
        items: JSON.stringify(cartItems),
      },
      theme: {
        color: '#DBAE6E',
      },
      modal: {
        ondismiss: () => {
          setPaymentStatus('failed');
          setLoading(false);
          onPay(false);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setPaymentStatus('failed');
        setLoading(false);
        onPay(false);
      });
      rzp.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      setPaymentStatus('failed');
      setLoading(false);
      onPay(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-primary dark:bg-primary-dark bg-opacity-80 flex items-center justify-center z-50"
    >
      <div className="bg-primary dark:bg-primary-dark text-white p-6 rounded-lg max-w-md w-full border border-[#DBAE6E]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-[#DBAE6E]">Payment</h2>
          <button onClick={onClose} className="text-white hover:text-[#DBAE6E]">
            <X size={24} />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-400">Total Amount:</p>
          <p className="text-2xl font-bold text-[#DBAE6E]">{currency} {total.toFixed(2)}</p>
          <p className="text-sm text-gray-400 mt-2">Order ID: {orderID}</p>
        </div>

        {!ready ? (
          <div className="w-full flex justify-center py-5">
            <div className="w-8 h-8 border-4 border-yellow-400 border-dashed rounded-full animate-spin"></div>
          </div>
        ) : (
          <motion.button
            onClick={handlePayment}
            className="w-full py-3 bg-[#DBAE6E] text-black rounded hover:bg-[#c9a055]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading || paymentStatus === 'success'}
          >
            {loading ? 'Processing...' : paymentStatus === 'success' ? 'Success!' : 'Pay Now'}
          </motion.button>
        )}

        {paymentStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-green-400 text-center mt-4 flex items-center justify-center gap-2"
          >
            <CheckCircle size={20} /> Payment Successful!
          </motion.div>
        )}
        {paymentStatus === 'failed' && (
          <p className="text-red-400 text-center mt-4">Payment Failed. Please try again.</p>
        )}
      </div>
    </motion.div>
  );
};

export default PaymentInterface;